/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    EOfferState,
    ISpecOfferBenefit,
    ISpecOfferCost,
    ISpecOfferFeature,
    TPromoChildOffer,
} from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/dto/ISpecOffer';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import RadioGroup from '@r1-frontend/ui-react/components/controls/radioGroup';
import { Label } from '@r1-frontend/ui-react/components/label';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import Accordion from '@r1-frontend/ui-react/deprecated/Accordion';
import { IndentContainer, ListContainer, NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import LabelsList from '~/src/features/specialOffersContainer/components/LabelsList';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { useConnectOffer } from '~/src/features/specialOffersContainer/hooks';
import { IProps } from '~/src/features/specialOffersContainer/types';

import { Benefit } from './components/Benefit';
import { OfferChannels } from './components/Channels';
import { OfferFeatures } from './components/Features';

import * as ST from './styled';

export const LkPromotionalPopup = ({
   offer,
   isPopupOpen,
   callbackMap,
   onClose,
}: IProps<typeof DefaultCallback>) => {
    const {
        title = '',
        images = [],
        navigation,
        childOffers,
        colorLabels,
        channels,
        advertising,
        state,

        /**
         * Нужно для биллинга, следит за состоянием обновления
         * вызванного ~/store/actions/specOffers/index -> viewOffer
        */
        isLoading,
        isConnecting,
    } = offer || {};
    const [detailImage] = images;

    const { isLocalLoading, activateButtonText, connectOffer } = useConnectOffer(callbackMap, offer);

    const [isTermsOpened, setIsTermsOpened] = useState(false);
    const [selectedChildOfferId, setSelectedChildOfferId] = useState<TPromoChildOffer['id'] | undefined>();

    const {
        description,
        features = [],
        terms,
        cost,
        benefits,
    } = useMemo(
        () => childOffers?.find(childOffer => childOffer.id === selectedChildOfferId) || offer || ({
            description: '',
            features: [] as ISpecOfferFeature[],
            terms: null,
            cost: null,
            benefits: [] as ISpecOfferBenefit[],
        }),
        [selectedChildOfferId, offer, childOffers],
    );

    const isShowButton = useMemo(
        () => navigation?.type !== 'promoPacket' || state !== EOfferState.CONNECTED,
        [navigation?.type, state],
    );

    useEffect(() => {
        const [first] = childOffers || [];
        first && setSelectedChildOfferId(first.id);
    }, [childOffers]);

    const getCost = useCallback(
        (value: keyof Pick<ISpecOfferCost, 'full' | 'withDiscount'>) => cost
            ? `${cost[value]} ${RubleSymbol}${cost.type === 'month' ? '/мес' : ''}`
            : null,
        [cost],
    );

    const toggleDetails = () => {
        if (!isTermsOpened && offer) {
            specialOfferAnalytics.showOfferDetails(offer);
        }

        setIsTermsOpened(prev => !prev);
    };

    return <ST.Popup
        onClose={onClose}
        isOpen={isPopupOpen}
        data-test='lkPromotionalPopup-root'
    >
        <ST.ImageBackground>
            {advertising && (
                <NonStaticContainer
                    $desktop={{ $bottom: 24, $right: 24 }}
                    $mobile={{ $bottom: 16, $right: 16 }}
                >
                    <AdvertisingLabelWithTooltip
                        onClick={e => e.stopPropagation()}
                        companyName={advertising.companyName}
                        erid={advertising.erid}
                        tooltip={{
                            position: 'top-right',
                            zIndex: 1024,
                        }}
                    />
                </NonStaticContainer>
            )}
            <ST.HeaderImage
                src={detailImage?.url || ''}
                alt={`изображение акции ${title || ''}`}
                visibleByDefault
            />
        </ST.ImageBackground>
        <ST.BodyCard
            key='body-root'
            data-test='lkPromotionalPopup-body-root'
        >
            {colorLabels && (
                <LabelsList labels={colorLabels}/>
            )}
            <ST.Title>
                {title}
            </ST.Title>

            {!!childOffers?.length && (
                <ST.ChildOffersWrapper>
                    <RadioGroup
                        options={childOffers.map(childOffer => ({
                            id: childOffer.id,
                            value: childOffer.id,
                            label: childOffer.title,
                        }))}
                        selectedValue={selectedChildOfferId}
                        onChange={setSelectedChildOfferId}
                    />
                </ST.ChildOffersWrapper>
            )}

            {description && (
                <ST.Description
                    dangerouslySetInnerHTML={{ __html: description }}
                    data-test='lkPromotionalPopup-Description-root'
                />
            )}

            <OfferFeatures features={features} />
            <OfferChannels channels={channels} />

            {!!benefits?.length && (
                <ST.BenefitsContainer data-test='lkPromotionalPopup-BenefitsContainer'>
                    {navigation?.type === 'promoPacket' &&
                        <ST.PopupSectionTitle>
                            В стоимость входит:
                        </ST.PopupSectionTitle>
                    }
                    {
                        benefits?.map(({ id: benefitId, ...benefit }) =>
                            <Benefit
                                key={benefitId}
                                {...benefit}
                            />)
                    }
                </ST.BenefitsContainer>
            )}

            {terms?.description && (
                <ST.AccordionWrapper>
                    <Accordion
                        withDividingBorder={false}
                        customTheme={ST.AccordionTheme}
                        title='Подробные условия'
                        data-test='lkPromotionalCard-terms'
                        toggle={toggleDetails}
                    >
                        <IndentContainer $united={[8, 0]}>
                            <ListContainer $direction={'column'} $wrap={'nowrap'} $size={8}>
                                <Paragraph4 $color={COLORS.TextInfo} dangerouslySetInnerHTML={{ __html: terms.description }} />
                                {terms?.pdf &&
                                    <ST.TermsLink href={terms.pdf}>Полные условия акции</ST.TermsLink>
                                }
                            </ListContainer>
                        </IndentContainer>
                    </Accordion>
                </ST.AccordionWrapper>
            )}

            <ST.BottomContainer childLength={[cost, true].filter(Boolean).length}>
                {cost && (
                    <ST.AllPricesContainer data-test='lkPromotionalPopup-AllPricesContainer-root'>
                        <ST.PricesContainer>
                            {typeof cost.withDiscount !== 'undefined'
                                ? <>
                                    <ST.Price>
                                        {getCost('withDiscount')}
                                    </ST.Price>
                                    <ST.OldPrice>
                                        {getCost('full')}
                                    </ST.OldPrice>
                                </>
                                : <>
                                    <ST.Price>
                                        {getCost('full')}
                                    </ST.Price>
                                </>}
                        </ST.PricesContainer>
                        {cost.description && (
                            <Label type={'LabelsText_2'}>{cost.description}</Label>
                        )}
                    </ST.AllPricesContainer>
                )}
                {isShowButton && (
                    <ButtonGroup>
                        <Button
                            bType={'action'}
                            loading={isConnecting || isLocalLoading}
                            disabled={isLoading}
                            data-test='lkPromotionalPopup-button-connect'
                            onClick={connectOffer}
                        >
                            {activateButtonText}
                        </Button>
                    </ButtonGroup>
                )}
            </ST.BottomContainer>
        </ST.BodyCard>
    </ST.Popup>;
};

export default LkPromotionalPopup;
